import {
    roundCentsUpToNearestHundredDollars,
    roundCentsUpToNearestThousandDollars,
} from '@fruitful/fruitils';
import { MoneyMapCategoryToAccountColorMap } from '@fruitful/money-map';
import {
    AccountOwnership,
    HolisticCategory,
    MoneyMapAccountStatus,
    MoneyMapAccountType,
    type MoneyMapAccountUpsertDto,
    MoneyMapCategory,
    MoneyMapRuleGoalType,
    type MoneyMapRuleGroupUpsertDto,
    MoneyMapRuleType,
    type MoneyMapRuleUpsertDto,
    PaycheckRoutingRuleType,
    Portfolio,
    RiskLevel,
    SupportedApexAccountRegistrationType,
    TimeHorizon,
} from '@fruitful/types';
import { FRUITFUL_ICON_KEY } from 'smoothie';

import {
    type MembershipDetails,
    MembershipType,
} from '../../entities/entityTypes';
import {
    PRESET_DEBT_PAY_DOWN_ACCOUNT_NAME,
    PRESET_EMERGENCY_FUND_ACCOUNT_NAME,
} from '../types/GoalPresets';
import { getAccountOwnershipAndPrimaryUser } from './moneyMapUtils';

const buildGoalCashAccountUpsertDto = (
    membership: MembershipDetails,
    name: string,
    category: HolisticCategory,
): MoneyMapAccountUpsertDto => {
    const { ownership, primaryUserId } =
        getAccountOwnershipAndPrimaryUser(membership);

    return {
        membershipId: membership.id,
        type: MoneyMapAccountType.INTERNAL_BANKING,
        status: MoneyMapAccountStatus.PROPOSED,
        createParameters: {
            name,
            ownership,
            primaryUserId,
            category,
            virtualCard: false,
            physicalCard: false,
            icon: FRUITFUL_ICON_KEY,
            color: MoneyMapCategoryToAccountColorMap[
                MoneyMapCategory.GOALS
            ] as string,
        },
    };
};

const buildRetirementAccountUpsertDto = ({
    membership,
    name,
    primaryUserId,
}: {
    membership: MembershipDetails;
    name: string;
    primaryUserId: string;
}): MoneyMapAccountUpsertDto => {
    return {
        membershipId: membership.id,
        type: MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT,
        status: MoneyMapAccountStatus.PROPOSED,
        createParameters: {
            name,
            ownership: AccountOwnership.INDIVIDUAL,
            primaryUserId,
            apexAccountType:
                SupportedApexAccountRegistrationType.RegistrationIraRoth,
            apexPortfolio: Portfolio.CORE,
            apexRiskLevel: RiskLevel.TEN,
            apexTimeHorizon: TimeHorizon.AVERAGE,
            icon: FRUITFUL_ICON_KEY,
            color: MoneyMapCategoryToAccountColorMap[
                MoneyMapCategory.GOALS
            ] as string,
        },
    };
};

const buildRetirementRules = ({
    membership,
    moneyMapId,
    priority,
}: {
    membership: MembershipDetails;
    moneyMapId: string;
    priority: number;
}): MoneyMapRuleUpsertDto[] => {
    if (
        membership.membershipType === MembershipType.JOINT &&
        membership.users.length > 1
    ) {
        return membership.users.map(({ firstName, id }) => {
            const description = `${firstName} Retirement`;

            return {
                moneyMapId,
                type: MoneyMapRuleType.GOAL,
                goalType: MoneyMapRuleGoalType.CONTRIBUTE_INDEFINITELY,
                description,
                priority,
                routingRuleType: PaycheckRoutingRuleType.PERCENTAGE,
                percentage: 0.02,
                moneyMapAccount: buildRetirementAccountUpsertDto({
                    membership,
                    name: description,
                    primaryUserId: id,
                }),
            };
        });
    }

    return [
        {
            moneyMapId,
            type: MoneyMapRuleType.GOAL,
            goalType: MoneyMapRuleGoalType.CONTRIBUTE_INDEFINITELY,
            description: 'Retirement',
            priority,
            routingRuleType: PaycheckRoutingRuleType.PERCENTAGE,
            percentage: 0.05,
            moneyMapAccount: buildRetirementAccountUpsertDto({
                membership,
                name: 'Retirement',
                primaryUserId: membership?.users[0]?.id as string,
            }),
        },
    ];
};

export const buildInitialMoneyMapGoalSetGroups = ({
    membership,
    moneyMapId,
    currentMonthlyBillsCents,
    currentMonthlySpendCents,
}: {
    membership: MembershipDetails;
    moneyMapId: string;
    currentMonthlyBillsCents: number;
    currentMonthlySpendCents: number;
}): MoneyMapRuleGroupUpsertDto[] => {
    const isJointMembership =
        membership.membershipType === MembershipType.JOINT &&
        membership.users.length > 1;
    const monthlyBillsAndSpendCents =
        currentMonthlyBillsCents + currentMonthlySpendCents;
    const oneMonthEmergencyFundTargetBalanceCents =
        roundCentsUpToNearestHundredDollars(monthlyBillsAndSpendCents);
    const fullEmergencyFundTargetBalanceCents =
        roundCentsUpToNearestThousandDollars(monthlyBillsAndSpendCents * 3);

    return [
        {
            moneyMapId,
            priority: 3,
            percentage: 1,
            name: 'Foundation & Stability',
            moneyMapRules: [
                {
                    moneyMapId,
                    type: MoneyMapRuleType.GOAL,
                    goalType: MoneyMapRuleGoalType.BUILD_UP_TARGET_BALANCE,
                    description: '1 Month Emergency Fund',
                    priority: 3,
                    routingRuleType: PaycheckRoutingRuleType.MINIMUM_BALANCE,
                    percentage: 1,
                    targetBalanceCents: oneMonthEmergencyFundTargetBalanceCents,
                    moneyMapAccount: buildGoalCashAccountUpsertDto(
                        membership,
                        PRESET_EMERGENCY_FUND_ACCOUNT_NAME,
                        HolisticCategory.SAVE,
                    ),
                },
            ],
        },
        {
            moneyMapId,
            priority: 4,
            percentage: 1,
            name: 'Financial Health',
            moneyMapRules: [
                {
                    moneyMapId,
                    type: MoneyMapRuleType.GOAL,
                    goalType: MoneyMapRuleGoalType.BUILD_UP_TARGET_BALANCE,
                    description: 'Full Emergency Fund',
                    priority: 4,
                    routingRuleType: PaycheckRoutingRuleType.MINIMUM_BALANCE,
                    percentage: isJointMembership ? 0.16 : 0.15,
                    targetBalanceCents: fullEmergencyFundTargetBalanceCents,
                    moneyMapAccount: buildGoalCashAccountUpsertDto(
                        membership,
                        PRESET_EMERGENCY_FUND_ACCOUNT_NAME,
                        HolisticCategory.SAVE,
                    ),
                },
                {
                    moneyMapId,
                    type: MoneyMapRuleType.GOAL,
                    goalType: MoneyMapRuleGoalType.DEBT_PAY_DOWN,
                    description: PRESET_DEBT_PAY_DOWN_ACCOUNT_NAME,
                    priority: 4,
                    routingRuleType: PaycheckRoutingRuleType.MINIMUM_BALANCE,
                    percentage: 0.8,
                    // Omit balance so guides must enter a real loan amount before publish/finalize
                    // (0 breaks Income Split target-balance rule generation).
                    targetBalanceCents: null,
                    moneyMapAccount: buildGoalCashAccountUpsertDto(
                        membership,
                        PRESET_DEBT_PAY_DOWN_ACCOUNT_NAME,
                        HolisticCategory.SAVE,
                    ),
                },
                ...buildRetirementRules({
                    membership,
                    moneyMapId,
                    priority: 4,
                }),
            ],
        },
    ];
};
